import { useState, useEffect, useRef } from 'react';
import { transformWNBASchedule } from '../utils/wnbaHelpers';
import { normalizeGamesBySport } from '../utils/gameNormalizer';
import { isGameFinished } from '../utils/constants';

// Parse the earliest usable timestamp off a game record. Returns ms since
// epoch, or NaN when nothing parseable exists. Keeps the same field chain
// DataTable/GameHeader use (startTimeEST first, then commence_time, then
// the broader fallbacks).
const getGameStartMs = (g) => {
  const candidate = g.startTimeEST || g.commence_time || g.startTime || g.gameTime || g.gameDate;
  if (!candidate) return NaN;
  const t = new Date(candidate).getTime();
  return Number.isFinite(t) ? t : NaN;
};

const sortGamesByStatus = (games) => {
  // Decorate with original index so we fall back to stable API order when
  // start times are missing or tied.
  const indexed = games.map((game, idx) => ({ game, idx }));
  indexed.sort((a, b) => {
    const aFin = isGameFinished(a.game.status);
    const bFin = isGameFinished(b.game.status);
    // Finals always sink to the bottom.
    if (aFin !== bFin) return aFin ? 1 : -1;
    // Among same-status games, order by soonest-to-latest start time so
    // multi-day `/next?days=N` responses (NCAAB today → Thu, etc.) read
    // chronologically with today's games on top.
    const at = getGameStartMs(a.game);
    const bt = getGameStartMs(b.game);
    if (Number.isFinite(at) && Number.isFinite(bt) && at !== bt) return at - bt;
    if (Number.isFinite(at) && !Number.isFinite(bt)) return -1;
    if (!Number.isFinite(at) && Number.isFinite(bt)) return 1;
    // Fall back to original API order on ties / missing times.
    return a.idx - b.idx;
  });
  return indexed.map(({ game }) => game);
};

export const useGamesData = (selectedDate = null, sport = 'mlb') => {
  const [games, setGames] = useState([]);
  const [gamesMetadata, setGamesMetadata] = useState(null);

  const fetchGamesMeta = async (date = null, silent = false) => {
    try {
      const dateParam = date || selectedDate;
      let endpoint;

      if (sport === 'ncaaf') {
        // NCAAF uses different endpoint structure
        endpoint = `/api/games/ncaaf/schedule/week/today`;

        if (dateParam) {
          // For NCAAF, if a specific date is selected, we still use "today"
          // as the API determines the appropriate week automatically
          endpoint = `/api/games/ncaaf/schedule/week/today`;
        }
      } else if (sport === 'soccer') {
        // Soccer uses /next endpoint by default, or date-specific when provided
        endpoint = `/api/games/soccer/next`;

        if (dateParam) {
          // Convert MM/DD/YYYY to YYYY-MM-DD format for API
          const parts = dateParam.split('/');
          const apiDate = `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
          endpoint = `/api/games/soccer/${apiDate}`;
        }
      } else if (sport === 'nhl' || sport === 'nba' || sport === 'wnba' || sport === 'ncaab' || sport === 'mlb') {
        // NHL, NBA, WNBA, NCAAB, and MLB use /next endpoint (date-specific when provided).
        // WNBA is offseason — pin to the final-day scoreboard so the panel
        // isn't empty.
        endpoint = sport === 'wnba'
          ? `/api/games/wnba/20261031`
          : `/api/games/${sport}/next`;

        if (dateParam) {
          // Convert MM/DD/YYYY to YYYY-MM-DD format for API
          const parts = dateParam.split('/');
          const apiDate = `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
          endpoint = `/api/games/${sport}/${apiDate}`;
        }
      } else {
        // Other sports use the original endpoint structure
        endpoint = `/api/games/${sport}/today`;

        if (dateParam) {
          // Convert MM/DD/YYYY to YYYY-MM-DD format for API
          const parts = dateParam.split('/');
          const apiDate = `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
          endpoint = `/api/games/${sport}/${apiDate}`;
        }
      }
      
      let response = await fetch(endpoint);

      // Add this check:
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      let data = await response.json();

      // Handle different API response formats
      if (Array.isArray(data)) {
        // Legacy format: direct array (MLB)
        console.log('Using legacy array format');
        const sortedGames = sortGamesByStatus(data);
        setGames(sortedGames);
        setGamesMetadata({
          sport: sport,
          totalGames: data.length,
          format: 'legacy'
        });
      } else if (data && typeof data === 'object' && data.games && Array.isArray(data.games)) {
        // New format: object with games array (MLB/WNBA from frontend API)
        console.log('Using new object format for', data.sport || sport);
        
        // Normalize games based on sport type
        const normalizedGames = normalizeGamesBySport(data.games, data.sport || sport);
        const sortedGames = sortGamesByStatus(normalizedGames);
        
        setGames(sortedGames);
        setGamesMetadata({
          sport: data.sport,
          sportDisplayName: data.sportDisplayName,
          date: data.date,
          gameDateKey: data.gameDateKey,
          totalGames: data.totalGames,
          timestamp: data.timestamp,
          format: 'new'
        });
      } else if (data && data.error) {
        // Error response
        console.error('API returned error:', data.error);
        throw new Error(data.error);
      } else {
        // Unexpected format
        console.error('Unexpected API response format:', data);
        throw new Error('Unexpected response format');
      }
      
    } catch (err) {
      console.error('Failed to fetch games:', err);
      // Silent poll / visibility refresh — leave the already-loaded list on
      // screen. Mobile browsers throttle background tabs and often kill the
      // in-flight fetch, and we don't want the App-level empty-state overlay
      // to flash on return.
      if (!silent) {
        setGames([]);
        setGamesMetadata({
          sport: sport,
          totalGames: 0,
          error: err.message,
          format: 'error'
        });
      }
    }
  };

  useEffect(() => {
    fetchGamesMeta();
    const gameStatusInterval = setInterval(() => fetchGamesMeta(null, true), 30000);

    // Handle page visibility changes (screen lock/unlock)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Page became visible again (screen unlocked)
        console.log('Page visible again, refetching games...');
        fetchGamesMeta(null, true);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearInterval(gameStatusInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [selectedDate, sport]);

  return { games, gamesMetadata, refetchGames: fetchGamesMeta };
};